import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { toAbsoluteUrl, SITE_URL, getOgImageWithFallback } from '../../lib/seo';

interface MetaProps {
  title?: string;
  description?: string;
  noindex?: boolean;
  canonicalPath?: string;

  // Enhanced Props
  og?: {
    title?: string;
    description?: string;
    image?: string;
    type?: 'website' | 'article';
    url?: string;
  };
  twitter?: {
    card?: 'summary' | 'summary_large_image';
    title?: string;
    description?: string;
    image?: string;
  };
  alternates?: Array<{
    lang: string;
    href: string;
  }>;
  jsonLd?: Record<string, any>;
}

export const Meta: React.FC<MetaProps> = ({
  title,
  description,
  noindex = false,
  canonicalPath,
  og,
  twitter,
  alternates,
  jsonLd,
}) => {
  const { content, language } = useLanguage();
  const location = useLocation();

  // Derive HTML lang attribute from current language
  const htmlLang = language === 'zh-hk' ? 'zh-HK' : 'en';

  // Defaults
  const finalTitle = title || content.meta.title;
  const finalDesc = description || content.meta.description;

  // Strict Canonical Logic
  // If canonicalPath is provided, use it. Otherwise derive from location.
  const cleanPath = canonicalPath
    ? canonicalPath
    : location.pathname.endsWith('/') && location.pathname !== '/'
      ? location.pathname.slice(0, -1)
      : location.pathname;

  const canonicalUrl = toAbsoluteUrl(cleanPath);
  const domain = SITE_URL.replace(/^https?:\/\//, '').replace(/\/$/, '');

  // Image Logic: Prefer specific prop, fallback to default OG image
  const rawImage = og?.image || twitter?.image;
  const imageUrl = getOgImageWithFallback(rawImage);

  return (
    <Helmet>
      <html lang={htmlLang} />

      {/* 1. Basic Tags */}
      <title>{finalTitle}</title>
      <meta name="description" content={finalDesc} />
      <meta name="robots" content={noindex ? 'noindex,follow' : 'index,follow'} />
      <link rel="canonical" href={canonicalUrl} />

      {/* 2. Open Graph */}
      <meta property="og:site_name" content="Natuvía" />
      <meta property="og:title" content={og?.title || finalTitle} />
      <meta property="og:description" content={og?.description || finalDesc} />
      <meta property="og:type" content={og?.type || 'website'} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:image:secure_url" content={imageUrl} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />

      {/* 3. Twitter */}
      <meta name="twitter:card" content={twitter?.card || 'summary_large_image'} />
      <meta name="twitter:title" content={twitter?.title || finalTitle} />
      <meta name="twitter:description" content={twitter?.description || finalDesc} />
      <meta name="twitter:image" content={imageUrl} />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:domain" content={domain} />

      {/* 4. Hreflang Alternates */}
      {alternates &&
        alternates.map(alt => (
          <link key={alt.lang} rel="alternate" hrefLang={alt.lang} href={alt.href} />
        ))}

      {/* 5. JSON-LD */}
      {jsonLd && <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>}
    </Helmet>
  );
};
